import React, { useState } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import { FormControl, FormHelperText, Link } from '@material-ui/core';

import { TextInput } from './TextInput';

const validateTenantName = (
  name: string,
  maxLength?: number,
): string | undefined => {
  if (!name) return 'Name is required';
  if (maxLength !== undefined && name.length > maxLength)
    return `Name must be at most ${maxLength} characters (${name.length} given)`;
  if (!/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/.test(name))
    return 'Name must consist of lower case alphanumeric characters or -';
  return undefined;
};

export const KaapNameInput = (props: FieldExtensionComponentProps<string>) => {
  const {
    onChange,
    required,
    disabled,
    rawErrors,
    formData,
    formContext,
    schema,
  } = props;

  // Taken from the field's JSON schema rather than hardcoded, so this stays in
  // step with `maxLength` in the template and stays inert where no limit is set
  // (edit-tenant reuses this field to show the full, longer tenant name).
  const maxLength = schema?.maxLength;

  const [nameError, setNameError] = useState('');

  const selectedStage: string | undefined = (formContext as Record<string, any>)
    ?.formData?.stage;
  const tenantId: string | undefined = (formContext as Record<string, any>)
    ?.formData?.tenantId;

  const onNameChange = (e: any) => {
    const errors = validateTenantName(e.target.value, maxLength);
    if (errors) {
      setNameError(errors);
    } else {
      setNameError('');
    }

    onChange(e.target.value);
  };

  return (
    <FormControl
      fullWidth
      required={required}
      error={rawErrors?.length > 0 && !formData}
    >
      <TextInput
        id="tenant-name"
        name="name"
        value={formData ?? ''}
        required={required}
        disabled={disabled}
        onChange={onNameChange}
        textBefore={tenantId ? '' : `${selectedStage}-${tenantId ?? '****'}-`}
        error={Boolean(nameError)}
        helperText={
          nameError || (
            <span>
              For example: {selectedStage}-****-secrets-sync, where **** is
              reserved for tenant ID.{' '}
              {maxLength !== undefined && `At most ${maxLength} characters. `}
              <Link
                target="_blank"
                href="https://frontstage.tomtomgroup.com/docs/default/component/kaap-documentation/#tenants"
              >
                More details.
              </Link>
            </span>
          )
        }
      />
      {rawErrors?.map((err, index) => (
        <FormHelperText key={index} error>
          {err}
        </FormHelperText>
      ))}
    </FormControl>
  );
};
